import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline, Edit, Psychology } from '@mui/icons-material';
import { DocumentOverview } from '../Document/DocumentOverview';
import { IDocument } from '../Interfaces/IDocument';
import {
  IKnowledgeItem,
  TARGET_AI_OPTIONS,
  targetAiLabel,
} from '../Interfaces/IKnowledgeItem';

interface IProps {
  /** Eigene Wissensergänzung (editierbar) — Child-Array am Requirement/Template. */
  items: IKnowledgeItem[];
  onChange: (items: IKnowledgeItem[]) => void;
  /** Vorgang: aus der Rechtsgeschäftsart geerbte Einträge (nur lesbar). */
  inheritedItems?: IKnowledgeItem[];
  /** Änderungen gesperrt (z. B. Maßnahme „Erledigt"). */
  locked?: boolean;
}

/**
 * #1244 — Wissensergänzung (KI-Hinweise/Prompts) je Maßnahme bzw. Maßnahmen-
 * Vorlage. Rein kontrolliert auf einem Array — die Persistenz erfolgt mit dem
 * jeweiligen fullObject (Vorgang bzw. Rechtsgeschäftsart), kein eigener Request.
 */
export const KnowledgeItemManager: React.FC<IProps> = ({
  items,
  onChange,
  inheritedItems = [],
  locked = false,
}) => {
  const [editItem, setEditItem] = useState<IKnowledgeItem | null>(null);

  const emptyItem = (): IKnowledgeItem => {
    const nextId =
      items.reduce((min, k) => Math.min(min, k.idKnowledgeItem), 0) - 1;
    return {
      idKnowledgeItem: nextId,
      idLegalRequirementTemplate: null,
      idLegalRequirement: null,
      TargetAI: 'all',
      Title: '',
      Content: '',
      activeKnowledgeItem: true,
      DocumentArray: [],
    };
  };

  const handleSave = () => {
    if (!editItem) return;
    if (!editItem.Title.trim()) return;
    const exists = items.some(
      k => k.idKnowledgeItem === editItem.idKnowledgeItem
    );
    const next = exists
      ? items.map(k =>
          k.idKnowledgeItem === editItem.idKnowledgeItem ? editItem : k
        )
      : [...items, editItem];
    onChange(next);
    setEditItem(null);
  };

  const handleDelete = (k: IKnowledgeItem) => {
    onChange(items.filter(x => x.idKnowledgeItem !== k.idKnowledgeItem));
  };

  const renderItem = (k: IKnowledgeItem, editable: boolean) => (
    <Card key={k.idKnowledgeItem} variant='outlined'>
      <CardContent sx={{ pb: 1.5 }}>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
          <Chip
            size='small'
            color='secondary'
            icon={<Psychology />}
            label={targetAiLabel(k.TargetAI)}
          />
          <Typography variant='subtitle2' sx={{ flex: 1 }}>
            {k.Title}
          </Typography>
          {!editable && (
            <Chip
              size='small'
              variant='outlined'
              label='aus Rechtsgeschäftsart'
            />
          )}
          {editable && (
            <>
              <Tooltip title='Bearbeiten'>
                <span>
                  <IconButton
                    size='small'
                    disabled={locked}
                    onClick={() => setEditItem({ ...k })}
                  >
                    <Edit fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Entfernen'>
                <span>
                  <IconButton
                    size='small'
                    disabled={locked}
                    onClick={() => handleDelete(k)}
                  >
                    <DeleteOutline fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
        {k.Content && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            {k.Content}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Alert
        severity='info'
        icon={<Psychology />}
        sx={{ mb: 2 }}
        action={
          <Button
            size='small'
            startIcon={<Add />}
            disabled={locked}
            onClick={() => setEditItem(emptyItem())}
          >
            Neu
          </Button>
        }
      >
        Diese Inhalte werden für die <b>KI-gestützte Prozessunterstützung</b>{' '}
        verwendet. Je Eintrag ist angegeben, für welche KI bzw. welchen
        Anwendungsbereich er vorgesehen ist. Änderungen werden beim Speichern
        übernommen.
      </Alert>

      {locked && (
        <Alert severity='info' sx={{ mb: 1 }}>
          Die Maßnahme ist mit Status „Erledigt" gespeichert — Wissensergänzung
          ist gesperrt.
        </Alert>
      )}

      {items.length === 0 ? (
        <Alert severity='info' variant='outlined'>
          Keine eigene Wissensergänzung hinterlegt.
        </Alert>
      ) : (
        <Stack spacing={1}>{items.map(k => renderItem(k, true))}</Stack>
      )}

      {inheritedItems.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant='caption' color='text.secondary'>
            Aus der Rechtsgeschäftsart geerbt (nur lesbar)
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {inheritedItems.map(k => renderItem(k, false))}
          </Stack>
        </Box>
      )}

      <Dialog
        open={editItem !== null}
        onClose={() => setEditItem(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {editItem && editItem.idKnowledgeItem < 0
            ? 'Neue Wissensergänzung'
            : 'Wissensergänzung bearbeiten'}
        </DialogTitle>
        <DialogContent>
          {editItem && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label='Ziel-KI / Anwendungsbereich'
                value={editItem.TargetAI}
                onChange={e =>
                  setEditItem({ ...editItem, TargetAI: e.target.value })
                }
              >
                {TARGET_AI_OPTIONS.map(o => (
                  <MenuItem key={o.key} value={o.key}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label='Titel'
                value={editItem.Title}
                onChange={e =>
                  setEditItem({ ...editItem, Title: e.target.value })
                }
                error={!editItem.Title.trim()}
              />
              <TextField
                label='Inhalt / Prompt'
                value={editItem.Content ?? ''}
                onChange={e =>
                  setEditItem({ ...editItem, Content: e.target.value })
                }
                multiline
                rows={6}
              />
              <DocumentOverview
                documentArray={(editItem.DocumentArray ?? []) as IDocument[]}
                setDocumentArray={(docs: IDocument[]) =>
                  setEditItem({ ...editItem, DocumentArray: docs })
                }
                allowUpload
                allowRemove
                title='PDF-/Dokument-Anhänge (für KI / Mitarbeiter)'
                titleVariant='subtitle1'
                isSendEmailPossible={false}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Abbruch</Button>
          <Button variant='contained' onClick={() => handleSave()}>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
